import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';

export interface MatrixPillProps {
  color: string;
  glowColor: string;
}

interface GridCell {
  char: string;
  opacity: number;
  speed: number;
  delay: number;
  sparkle: boolean;
}

interface StreakParams {
  duration: number;
  delay: number;
}

interface SparkleParams {
  left: number;
  top: number;
  duration: number;
  delay: number;
}

interface ParticleParams {
  char: string;
  left: number;
  top: number;
  bounce: number;
  duration: number;
  delay: number;
}

function getSparkleColor(i: number, fallback: string): string {
  if (i % 3 === 0) return '#ffffff';
  if (i % 2 === 0) return '#ffffffcc';
  return fallback;
}

const CHARS = '01\u30CE\u30D4\u30F3\u30B0NoPing><%#@&*!\u30A2\u30A4\u30A6\u30A8\u30AA\u30AB\u30AD\u30AF\u30B1\u30B3';
const COLS = 6;
const ROWS = 12;
const PILL_W = 36;
const PILL_H = 85;
const R = PILL_W / 2;
const STREAK_COUNT = 5;
const SPARKLE_COUNT = 10;
const PARTICLE_COUNT = 6;

export function MatrixPill({ color, glowColor }: MatrixPillProps) {
  const [grid, setGrid] = useState<GridCell[][]>([]);
  const [streaks, setStreaks] = useState<StreakParams[]>([]);
  const [sparkles, setSparkles] = useState<SparkleParams[]>([]);
  const [particles, setParticles] = useState<ParticleParams[]>([]);

  useEffect(() => {
    const nextGrid: GridCell[][] = [];
    for (let c = 0; c < COLS; c += 1) {
      const col: GridCell[] = [];
      for (let r = 0; r < ROWS; r += 1) {
        col.push({
          char: CHARS[Math.floor(Math.random() * CHARS.length)]!,
          opacity: Math.random() * 0.85 + 0.15,
          speed: 1 + Math.random() * 1.8,
          delay: Math.random() * 2.5,
          sparkle: Math.random() < 0.35,
        });
      }
      nextGrid.push(col);
    }
    setGrid(nextGrid);

    setStreaks(Array.from({ length: STREAK_COUNT }, () => ({ duration: 1.2 + Math.random() * 1.8, delay: Math.random() * 2.5 })));
    setSparkles(Array.from({ length: SPARKLE_COUNT }, () => ({ left: 10 + Math.random() * 80, top: 5 + Math.random() * 90, duration: 1 + Math.random() * 1.5, delay: Math.random() * 4 })));
    setParticles(Array.from({ length: PARTICLE_COUNT }, () => ({ char: CHARS[Math.floor(Math.random() * CHARS.length)]!, left: 5 + Math.random() * 90, top: 5 + Math.random() * 90, bounce: 10 + Math.random() * 15, duration: 2 + Math.random() * 3, delay: Math.random() * 4 })));
  }, []);

  const capsulePath = `M ${R} 0 A ${R} ${R} 0 0 1 ${PILL_W} ${R} L ${PILL_W} ${PILL_H - R} A ${R} ${R} 0 0 1 0 ${PILL_H - R} L 0 ${R} A ${R} ${R} 0 0 1 ${R} 0 Z`;
  const INSET = 1.5;
  const IR = R - INSET;
  const IW = PILL_W - INSET * 2;
  const IH = PILL_H - INSET * 2;
  const borderPath = `M ${IR + INSET} ${INSET} A ${IR} ${IR} 0 0 1 ${IW + INSET} ${IR + INSET} L ${IW + INSET} ${IH - IR + INSET} A ${IR} ${IR} 0 0 1 ${INSET} ${IH - IR + INSET} L ${INSET} ${IR + INSET} A ${IR} ${IR} 0 0 1 ${IR + INSET} ${INSET} Z`;

  return (
    <div className='relative mx-auto mb-3' style={{ width: PILL_W + 30, height: PILL_H + 20 }}>
      <div className='absolute rounded-full' style={{ left: -8, top: -10, width: PILL_W + 46, height: PILL_H + 40, background: 'radial-gradient(ellipse 60% 55% at 50% 48%, #000000ee, #000000bb 50%, transparent 80%)' }} />
      <div className='absolute' style={{ left: 3, top: 0, width: PILL_W + 24, height: PILL_H + 20, background: `radial-gradient(ellipse 55% 50% at 50% 48%, ${glowColor}50, ${glowColor}18 50%, transparent 80%)`, filter: 'blur(16px)' }} />
      <div className='absolute left-1/2 -translate-x-1/2' style={{ bottom: -4, width: '55%', height: 12, background: `radial-gradient(ellipse at center, ${glowColor}35, transparent)`, filter: 'blur(8px)' }} />

      <div className='relative' style={{ width: PILL_W, height: PILL_H, margin: '0 auto' }}>
        <div className='absolute inset-0 overflow-hidden' style={{ clipPath: `path("${capsulePath}")` }}>
          <div className='absolute inset-0' style={{ background: '#020202' }} />
          <div className='absolute inset-0' style={{ background: `radial-gradient(ellipse 65% 40% at 50% 35%, ${glowColor}20, transparent 70%)` }} />

          {grid.map((col, ci) => (
            <div key={ci} className='absolute top-0' style={{ left: `${(ci / COLS) * 100}%`, width: `${100 / COLS}%` }}>
              {col.map((cell, ri) => (
                <motion.span key={ri} className='block select-none text-center font-mono leading-tight' style={{ fontSize: '5px', color, textShadow: cell.sparkle ? `0 0 4px ${glowColor}, 0 0 10px ${glowColor}, 0 0 16px ${glowColor}90` : `0 0 3px ${glowColor}90` }} animate={cell.sparkle ? { opacity: [0.05, 1, 0.7, 1, 0.1, 0.9, 1, 0.05], scale: [1, 1.4, 1, 1.5, 0.9, 1.3, 1.1, 1] } : { opacity: [cell.opacity * 0.15, cell.opacity, cell.opacity * 0.3, cell.opacity * 0.1, cell.opacity * 0.95] }} transition={{ duration: cell.sparkle ? cell.speed * 0.6 : cell.speed, delay: cell.delay, repeat: Infinity, ease: 'linear' as const }}>
                  {cell.char}
                </motion.span>
              ))}
            </div>
          ))}

          {streaks.map((s, i) => (
            <motion.div key={`streak-${i}`} className='absolute' style={{ left: `${10 + i * 20}%`, width: i % 2 === 0 ? 2 : 1, height: '22%', background: `linear-gradient(to bottom, transparent, ${color}70, ${glowColor}, ${color}90, transparent)`, filter: 'blur(1px)' }} animate={{ y: ['-35%', '135%'] }} transition={{ duration: s.duration, delay: s.delay, repeat: Infinity, ease: 'linear' as const }} />
          ))}

          {sparkles.map((s, i) => (
            <motion.div key={`sparkle-${i}`} className='absolute rounded-full' style={{ width: i % 3 === 0 ? 3 : 2, height: i % 3 === 0 ? 3 : 2, left: `${s.left}%`, top: `${s.top}%`, background: getSparkleColor(i, glowColor), boxShadow: `0 0 3px #fff, 0 0 6px ${glowColor}, 0 0 12px ${glowColor}` }} animate={{ opacity: [0, 0, 1, 0.6, 1, 0, 0], scale: [0.3, 0.3, 1.5, 1, 1.3, 0.3, 0.3] }} transition={{ duration: s.duration, delay: s.delay, repeat: Infinity, ease: 'easeInOut' as const }} />
          ))}

          <motion.div className='pointer-events-none absolute start-0 w-full' style={{ height: 12, background: `linear-gradient(to bottom, transparent, ${glowColor}20, ${glowColor}10, transparent)` }} animate={{ y: ['-20%', '120%'] }} transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' as const }} />
          <motion.div className='pointer-events-none absolute start-0 w-full' style={{ height: 8, background: 'linear-gradient(to bottom, transparent, #ffffff10, #ffffff06, transparent)' }} animate={{ y: ['120%', '-20%'] }} transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' as const }} />
          <div className='pointer-events-none absolute' style={{ top: 3, left: '18%', width: '40%', height: 15, background: `radial-gradient(ellipse at center, ${glowColor}25, transparent)`, filter: 'blur(3px)' }} />
        </div>

        <svg className='pointer-events-none absolute inset-0' viewBox={`0 0 ${PILL_W} ${PILL_H}`} fill='none' style={{ filter: `drop-shadow(0 0 6px ${glowColor}80) drop-shadow(0 0 12px ${glowColor}50) drop-shadow(0 0 20px ${glowColor}30)` }}>
          <path d={borderPath} stroke={glowColor} strokeWidth='1.5' fill='none' opacity='0.9' />
          <path d={borderPath} stroke={glowColor} strokeWidth='3' fill='none' opacity='0.25' />
        </svg>

        <motion.div className='pointer-events-none absolute inset-0' animate={{ opacity: [0.6, 1, 0.6] }} transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' as const }}>
          <svg viewBox={`0 0 ${PILL_W} ${PILL_H}`} fill='none' style={{ filter: `drop-shadow(0 0 10px ${glowColor}) drop-shadow(0 0 24px ${glowColor}60)` }}>
            <path d={borderPath} stroke={glowColor} strokeWidth='1' fill='none' opacity='0.5' />
          </svg>
        </motion.div>
      </div>

      {particles.map((p, i) => (
        <motion.div key={`particle-${i}`} className='absolute select-none font-mono' style={{ fontSize: '5px', color, opacity: 0.3, left: `${p.left}%`, top: `${p.top}%`, textShadow: `0 0 6px ${glowColor}` }} animate={{ y: [0, -p.bounce, 0], opacity: [0.25, 0, 0.25] }} transition={{ duration: p.duration, delay: p.delay, repeat: Infinity, ease: 'easeInOut' as const }}>
          {p.char}
        </motion.div>
      ))}
    </div>
  );
}
